import React from "react";
import { View, Pressable } from "react-native";
import {
  CommonActions,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { ChevronLeft, X } from "lucide-react-native";
import {
  EMBER,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import { PLAN_BUILDER_FLOW } from "../PlanBuilderNavigator";

export default function PlanBuilderScreenLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigation = useNavigation();
  const route = useRoute();
  const capturePress = usePressCapture("PlanBuilderScreenLayout");
  const flow = useFlowAnalytics(PLAN_BUILDER_FLOW);

  /** Dismiss the plan builder and return to Home. Falls back to a Home
   *  navigation when there is nothing to go back to (e.g. direct entry). */
  const handleClose = () => {
    capturePress(pressLabel("Close plan builder"));
    // Closing the reveal is how the flow ends, not how it is abandoned —
    // the plan is already built and saved by the time that screen shows.
    if (route.name !== "PlanReveal") {
      flow.abandoned("closed", { step: route.name });
    }
    const parent = navigation.getParent();
    if (parent?.canGoBack()) {
      parent.goBack();
    } else {
      navigation.dispatch(CommonActions.navigate({ name: "Home" }));
    }
  };

  return (
    <GlassScreenLayout>
      <View
        style={{
          flex: 1,
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
      >
        {/* Glass card content with header inside */}
        <View style={{ ...GLASS_CARD_STYLE, flex: 1, marginBottom: 16 }}>
          {/* Back + Close inside the card */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingHorizontal: 16,
              paddingTop: 16,
              paddingBottom: 4,
            }}
          >
            {navigation.canGoBack() ? (
              <Pressable
                onPress={() => {
                  capturePress(pressLabel("Back"));
                  navigation.goBack();
                }}
                accessibilityRole="button"
                accessibilityLabel="Back"
                hitSlop={12}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: `${EMBER.colors.textFaint}15`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ChevronLeft size={18} color={EMBER.colors.text} />
              </Pressable>
            ) : (
              <View style={{ width: 32 }} />
            )}
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel="Close plan builder"
              hitSlop={12}
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: `${EMBER.colors.textFaint}15`,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={16} color={EMBER.colors.text} />
            </Pressable>
          </View>

          {children}
        </View>
      </View>
    </GlassScreenLayout>
  );
}
